import * as Sentry from "@sentry/react";

type SentryConfig = {
  enabled: boolean;
  environment: string;
  dsn: string;
  tracesSampleRate: number;
  replaysOnErrorSampleRate: number;
};

const SENTRY_CONFIG_URL = "/config/sentry.json";

const DEFAULT_SENTRY_CONFIG: SentryConfig = {
  enabled: false,
  environment: "local",
  dsn: "",
  tracesSampleRate: 0.2,
  replaysOnErrorSampleRate: 1,
};

export let isSentryEnabled = false;

const normalizeSentryConfig = (value: unknown): SentryConfig => {
  const source = value && typeof value === "object" ? value : {};
  const config = source as Record<string, unknown>;

  return {
    enabled: config.enabled === true,
    environment:
      typeof config.environment === "string"
        ? config.environment
        : DEFAULT_SENTRY_CONFIG.environment,
    dsn: typeof config.dsn === "string" ? config.dsn : DEFAULT_SENTRY_CONFIG.dsn,
    tracesSampleRate:
      typeof config.tracesSampleRate === "number"
        ? config.tracesSampleRate
        : DEFAULT_SENTRY_CONFIG.tracesSampleRate,
    replaysOnErrorSampleRate:
      typeof config.replaysOnErrorSampleRate === "number"
        ? config.replaysOnErrorSampleRate
        : DEFAULT_SENTRY_CONFIG.replaysOnErrorSampleRate,
  };
};

export const initSentry = async (): Promise<void> => {
  let sentryConfig = DEFAULT_SENTRY_CONFIG;

  if (typeof window !== "undefined") {
    try {
      const response = await fetch(SENTRY_CONFIG_URL, { cache: "no-store" });

      if (response.ok) {
        sentryConfig = normalizeSentryConfig(await response.json());
      }
    } catch {
      // Keep Sentry disabled when config is unavailable.
    }
  }

  isSentryEnabled = sentryConfig.enabled && Boolean(sentryConfig.dsn);

  if (!isSentryEnabled) {
    return;
  }

  Sentry.init({
    dsn: sentryConfig.dsn,
    environment: sentryConfig.environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: sentryConfig.tracesSampleRate,
    replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
  });
};

export { Sentry };
